import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  memo,
  type KeyboardEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import { TEAM_LABELS } from '@kalkuel-inbox/shared';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  StickyNote,
  Paperclip,
  X,
  Phone,
  Mail,
  ExternalLink,
  Sparkles,
  MessageSquareReply,
  Check,
  CheckCheck,
  AlertCircle,
  Loader2,
  CalendarClock,
  ArrowDown,
  Star,
  LayoutTemplate,
  MessagesSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { useWsEvent, wsClient } from '../lib/ws';
import { formatTime, formatDate, formatRelative } from '../lib/format';
import { Badge, Button, ErrorState, EmptyState, InfoHint } from '../components/ui';
import { ConversationCrmSidebar } from '../components/ConversationCrmSidebar';
import { MessageAttachment } from '../components/MessageAttachment';
import { Linkified } from '../lib/linkify';
import { ReactionTrigger, ReactionChips } from '../components/ReactionBar';
import { QuickReplyPicker } from '../components/QuickReplyPicker';
import { TemplateVarsDialog, needsTemplateVars } from '../components/TemplateVarsDialog';
import { TemplateGalleryDialog } from '../components/TemplateGalleryDialog';
import { ConversationActionsMenu } from '../components/ConversationActionsMenu';
import { templateDisplayName } from '../lib/templateDisplay';
import { dateSeparatorLabel, isSameDay, shouldGroupWithPrevious } from '../lib/chatGrouping';
import type {
  ConversationDetail,
  Message,
  MessageAttachment as Attachment,
  InternalNote,
  Template,
  AppointmentInfo,
} from '../types';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Cache-Form der paginierten Nachrichten-Query (useInfiniteQuery). */
type MsgPage = { messages: Message[]; notes: InternalNote[]; prevCursor: string | null };
type MsgInfinite = { pages: MsgPage[]; pageParams: unknown[] };

interface PendingUpload {
  id: string;
  mimeType: string;
  filename: string | null;
}

export function ConversationPage() {
  const { conversationId } = useParams();
  const queryClient = useQueryClient();
  const [composeText, setComposeText] = useState('');
  // Spiegelt composeText, damit onError der Sende-Mutation den AKTUELLEN Eingabestand
  // lesen kann (Closures in Mutation-Callbacks sehen sonst einen veralteten Wert).
  const composeTextRef = useRef('');
  composeTextRef.current = composeText;
  // Interne Notizen werden NICHT mehr im Sende-Composer verfasst (Verwechslungsgefahr mit einer
  // Antwort an den Kontakt), sondern in der rechten Spalte — eigener, getrennter Eingabe-State.
  const [noteText, setNoteText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [varsDialogOpen, setVarsDialogOpen] = useState(false);
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [uploading, setUploading] = useState(false);
  const [quickRepliesOpen, setQuickRepliesOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const composeRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Scroll-Verwaltung: nur automatisch ans Ende scrollen, wenn der Nutzer ohnehin unten
  // ist; sonst „Neue Nachrichten"-Pille. Beim Nachladen älterer Nachrichten (Prepend)
  // die Scroll-Position erhalten (kein Springen).
  const [showNewBadge, setShowNewBadge] = useState(false);
  const atBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const prevFirstIdRef = useRef<string | null>(null);
  const prevCountRef = useRef(0);
  const olderSentinelRef = useRef<HTMLDivElement | null>(null);
  const tempIdRef = useRef(0);
  // Realtime-Invalidierung des Nachrichten-Caches bündeln: bei einem Schwall von
  // message.created/updated/status/reaction-Events (z.B. KI-Antwort-Stream) nicht je
  // Event refetchen, sondern Events über ein kurzes Fenster sammeln und EINMAL
  // invalidieren (analog InboxPage). Optimistisches Senden bleibt sofort sichtbar
  // (setQueryData in onMutate), Realtime-Updates kommen verzögert um < 1 Frame-Schwall.
  const msgRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    const topic = `conversation:${conversationId}`;
    wsClient.subscribe(topic);
    return () => wsClient.unsubscribe(topic);
  }, [conversationId]);

  // Aktuellen Nutzer einmalig laden (für eigene Reaktionen). sub == inbox-User-ID.
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    staleTime: Infinity,
    retry: false,
  });

  const {
    data: detail,
    isError: detailError,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.get<ConversationDetail>(`/api/conversations/${conversationId}`),
    enabled: Boolean(conversationId),
  });

  // Nachrichtenverlauf: neueste zuerst (Backend), beim Hochscrollen werden ältere
  // nachgeladen (useInfiniteQuery rückwärts; prevCursor = id der ältesten geladenen).
  const messagesKey = ['conversation-messages', conversationId] as const;
  const {
    data: messagesPages,
    fetchNextPage: fetchOlder,
    hasNextPage: hasOlder,
    isFetchingNextPage: isFetchingOlder,
  } = useInfiniteQuery({
    queryKey: messagesKey,
    enabled: Boolean(conversationId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const qs = pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : '';
      return api.get<{ messages: Message[]; notes: InternalNote[]; prevCursor: string | null }>(
        `/api/conversations/${conversationId}/messages${qs}`,
      );
    },
    getNextPageParam: (last) => last.prevCursor ?? undefined,
  });
  // Pages chronologisch aneinanderhängen (älteste Seite zuerst). Notizen liefert nur die
  // erste (neueste) Seite vollständig → von dort übernehmen.
  const allMessages = useMemo(
    () => (messagesPages ? [...messagesPages.pages].reverse().flatMap((p) => p.messages) : []),
    [messagesPages],
  );
  const allNotes = messagesPages?.pages[0]?.notes ?? [];

  // Messages + Notizen zu einem chronologischen Stream mischen + Render-Metadaten
  // (Datums-Trenner / Gruppierung / „letztes Element einer Gruppe") berechnen.
  // In useMemo, damit das nicht bei jedem Render (z.B. Tippen im Composer, Hover)
  // erneut sortiert/durchlaufen wird — nur wenn sich Nachrichten oder Notizen ändern.
  const streamRows = useMemo(() => {
    const combined: Array<{ kind: 'msg' | 'note'; item: Message | InternalNote }> = [
      ...allMessages.map((m) => ({ kind: 'msg' as const, item: m })),
      ...allNotes.map((n) => ({ kind: 'note' as const, item: n })),
    ].sort((a, b) => {
      const diff = new Date(a.item.createdAt).getTime() - new Date(b.item.createdAt).getTime();
      // PDF-Import liefert Minuten-Genauigkeit → mehrere Messages mit gleichem createdAt
      // möglich. Tiebreaker: id (cuid ist zeitlich monoton aufsteigend).
      return diff !== 0 ? diff : a.item.id.localeCompare(b.item.id);
    });

    // Render-Metadaten je Eintrag (WhatsApp-Stil):
    //  - showDateSeparator: erster Eintrag eines neuen Kalendertags.
    //  - groupedWithPrev: enge Gruppierung an die Vor-Nachricht (gleicher Absender,
    //    < 5 Min, selber Tag) → kleinerer Abstand. Notizen sind immer eigenständig.
    //  - isLastOfGroup: nur am LETZTEN Element einer Gruppe wird die Zeitmarke dauerhaft
    //    gezeigt (sonst erst beim Hover) → „eine Zeitmarke je Gruppe".
    return combined.map((entry, i) => {
      const prev = combined[i - 1];
      const next = combined[i + 1];
      const at = new Date(entry.item.createdAt);
      const showDateSeparator = !prev || !isSameDay(new Date(prev.item.createdAt), at);

      let groupedWithPrev = false;
      if (!showDateSeparator && entry.kind === 'msg' && prev?.kind === 'msg') {
        groupedWithPrev = shouldGroupWithPrevious(prev.item as Message, entry.item as Message);
      }

      let isLastOfGroup = true;
      if (entry.kind === 'msg' && next?.kind === 'msg') {
        const nextNewDay = !isSameDay(at, new Date(next.item.createdAt));
        if (!nextNewDay && shouldGroupWithPrevious(entry.item as Message, next.item as Message)) {
          isLastOfGroup = false;
        }
      }
      return { entry, showDateSeparator, groupedWithPrev, isLastOfGroup };
    });
  }, [allMessages, allNotes]);

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ items: Template[] }>('/api/templates'),
    enabled: Boolean(detail?.channel === 'WHATSAPP'),
  });

  // Gelesen-Markierung: einmal pro geöffneter Conversation.
  const markReadDoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId || !detail) return;
    if (markReadDoneRef.current === conversationId) return;
    markReadDoneRef.current = conversationId;
    api
      .post(`/api/conversations/${conversationId}/read`, {})
      .then(() => {
        // Liste aktualisieren, damit die Ungelesen-Markierung sofort verschwindet.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      })
      .catch(() => {
        /* best effort */
      });
  }, [conversationId, detail, queryClient]);

  // „tippt gerade…": eine humanisierte Auto-Antwort wird gerade verfasst (Webchat).
  // typing.start zeigt die Blase, message.created/typing.stop beendet sie; ein
  // Sicherheits-Timeout räumt sie auf, falls die Zustellung ausbleibt.
  const [contactTyping, setContactTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTyping = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = null;
    setContactTyping(false);
  };
  useEffect(() => clearTyping, [conversationId]);

  // Gebündelte Invalidierung des Nachrichten-Caches (300 ms-Fenster). Sammelt einen
  // Event-Schwall zu EINEM Refetch. Cleanup räumt einen offenen Timer beim Wechsel/Unmount.
  const scheduleMessagesRefetch = useCallback(() => {
    if (msgRefetchTimer.current) return; // bereits eingeplant
    msgRefetchTimer.current = setTimeout(() => {
      msgRefetchTimer.current = null;
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
    }, 300);
  }, [queryClient, conversationId]);
  useEffect(
    () => () => {
      if (msgRefetchTimer.current) clearTimeout(msgRefetchTimer.current);
      msgRefetchTimer.current = null;
    },
    [conversationId],
  );

  useWsEvent((event) => {
    if (event.conversationId !== conversationId) return;
    const payload = event.payload as { kind?: string; text?: string } | null;
    if (event.type === 'typing.start') {
      setContactTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setContactTyping(false), 20_000);
      return;
    }
    if (event.type === 'typing.stop') {
      clearTyping();
      return;
    }
    if (event.type === 'message.created' && payload?.kind === 'ai_suggestion') {
      setAiSuggestion(payload.text ?? '');
      return;
    }
    if (
      event.type === 'message.created' ||
      event.type === 'message.updated' ||
      event.type === 'note.created' ||
      event.type === 'message.status' ||
      event.type === 'reaction.updated'
    ) {
      if (event.type === 'message.created') clearTyping();
      scheduleMessagesRefetch();
    }
    if (event.type === 'conversation.updated') {
      // Nach Dedup-/Merge-Operationen können sich Messages der Konv geändert haben
      // (umgehängte Messages aus gemergten Import-Konvs). Detail sofort refreshen
      // (selten), Nachrichten-Cache über das gebündelte Fenster.
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      scheduleMessagesRefetch();
    }
  });

  const totalCount = allMessages.length + allNotes.length;
  const firstId = allMessages[0]?.id ?? null;

  // Scroll-Position des Containers verfolgen (für Smart-Autoscroll + Badge).
  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottomRef.current = distanceFromBottom < 80;
    if (atBottomRef.current && showNewBadge) setShowNewBadge(false);
  }

  // Nach jedem Render des Streams: Prepend (ältere) vs. Append (neue) unterscheiden.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = totalCount > prevCountRef.current;
    const prependedOlder =
      grew && prevCountRef.current > 0 && firstId !== null && firstId !== prevFirstIdRef.current;

    if (prependedOlder) {
      // Ältere oben eingefügt → Sichtfenster halten (Höhendifferenz aufaddieren).
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
    } else if (grew) {
      // Neue unten: ans Ende, wenn der Nutzer unten ist (oder Erst-Render); sonst Badge.
      if (atBottomRef.current || prevCountRef.current === 0) {
        el.scrollTop = el.scrollHeight;
      } else {
        setShowNewBadge(true);
      }
    }
    prevScrollHeightRef.current = el.scrollHeight;
    prevFirstIdRef.current = firstId;
    prevCountRef.current = totalCount;
  }, [totalCount, firstId]);

  function scrollToBottom() {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setShowNewBadge(false);
  }

  // Älteres Nachladen, wenn der Top-Sentinel sichtbar wird.
  useEffect(() => {
    const el = olderSentinelRef.current;
    if (!el || !hasOlder) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasOlder && !isFetchingOlder) fetchOlder();
      },
      { root: scrollRef.current, rootMargin: '150px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // totalCount bewusst NICHT als Dep: der Observer muss nicht bei jeder neuen Nachricht
    // neu aufgebaut werden. fetchOlder ist über Renders stabil (TanStack Query v5); der
    // Sentinel-Ref ist konstant. Scroll-Anchoring liegt im separaten useLayoutEffect.
  }, [hasOlder, isFetchingOlder, fetchOlder]);

  // 24h-Fenster: letzte EINGEHENDE Nachricht < 24h alt?
  const lastInbound = allMessages
    .filter((m) => m.direction === 'INBOUND')
    .reduce<Message | null>((acc, m) => {
      if (!acc) return m;
      return new Date(m.createdAt).getTime() > new Date(acc.createdAt).getTime() ? m : acc;
    }, null);
  const windowOpen = lastInbound
    ? Date.now() - new Date(lastInbound.createdAt).getTime() < TWENTY_FOUR_HOURS_MS
    : false;
  const isWhatsApp = detail?.channel === 'WHATSAPP';
  // Außerhalb des Fensters (oder kein Inbound): WhatsApp erlaubt eigentlich nur Vorlagen.
  // Wir SPERREN den Freitext aber NICHT mehr hart (sonst Sackgasse, da Vorlagen derzeit
  // Meta-zahlungsblockiert sind) — nur ein Hinweis; der Versuch ist erlaubt.
  const windowClosed = Boolean(isWhatsApp) && !windowOpen;

  const sendMessage = useMutation({
    mutationFn: async (input: {
      contentText?: string;
      templateName?: string;
      templateLang?: string;
      vars?: string[];
      // Per-Send-Werte für volle Template-Komponenten-Parität (nur bei Vorlagen MIT Komponenten
      // gesetzt; Body-only-Vorlagen lassen sie undefined → Attachment exakt wie bisher).
      headerVars?: string[];
      headerMedia?: { mediaId: string };
      buttonParams?: Array<{ subType: 'url' | 'quick_reply'; index: number; value: string }>;
      upload?: PendingUpload | null;
    }) => {
      if (!conversationId) throw new Error('no conversation');
      const attachments: Attachment[] = [];
      if (input.templateName) {
        attachments.push({
          type: 'template',
          templateName: input.templateName,
          language: input.templateLang ?? 'de',
          variables: input.vars ?? [],
          // Nur setzen, wenn vorhanden — sonst landet kein leeres Feld im Attachment (Body-only).
          ...(input.headerVars ? { headerVars: input.headerVars } : {}),
          ...(input.headerMedia ? { headerMedia: input.headerMedia } : {}),
          ...(input.buttonParams ? { buttonParams: input.buttonParams } : {}),
        });
      }
      if (input.upload) {
        attachments.push({
          type: mediaTypeForMime(input.upload.mimeType),
          mediaId: input.upload.id,
          filename: input.upload.filename ?? undefined,
        });
      }
      return api.post<Message>(`/api/conversations/${conversationId}/messages`, {
        contentText: input.contentText,
        attachments,
      });
    },
    // Optimistisch: freie Textnachrichten sofort als „wird gesendet…" anzeigen, Eingabe
    // sofort leeren. onSettled (invalidate) ersetzt den Stub durch die echte Nachricht.
    onMutate: async (input) => {
      const isOptimisticText = Boolean(input.contentText && !input.templateName && !input.upload);
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const prev = queryClient.getQueryData<MsgInfinite>(messagesKey);
      // prevText nur für den optimistischen Freitext-Pfad merken: nur dort wird der
      // Composer in onMutate geleert. Bei Vorlagen-/Upload-Sends bleibt composeText
      // unangetastet → es gibt nichts wiederherzustellen (sonst würde im Fehlerfall
      // alter Text fälschlich in den leeren Composer zurückkehren).
      const prevText = isOptimisticText ? composeText : '';
      if (isOptimisticText) {
        const temp: Message = {
          id: `temp-${tempIdRef.current++}`,
          conversationId: conversationId ?? '',
          direction: 'OUTBOUND',
          senderKind: 'USER',
          authorUserId: me?.sub ?? null,
          contentText: input.contentText ?? '',
          attachments: [],
          reactions: [],
          externalId: null,
          status: 'SENDING',
          errorReason: null,
          createdAt: new Date().toISOString(),
        };
        queryClient.setQueryData<MsgInfinite>(messagesKey, (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((p, i) =>
                  i === 0 ? { ...p, messages: [...p.messages, temp] } : p,
                ),
              }
            : old,
        );
        setComposeText('');
        atBottomRef.current = true; // eigene Nachricht → ans Ende scrollen
      }
      return { prev, prevText };
    },
    onSuccess: () => {
      setComposeText('');
      setSelectedTemplate(null);
      setPendingUpload(null);
    },
    onError: (err: Error, _input, ctx) => {
      // Optimistischen Stub zurückrollen (Cache auf den Vorzustand zurücksetzen).
      if (ctx?.prev) queryClient.setQueryData(messagesKey, ctx.prev);
      // Eingabetext NUR wiederherstellen, wenn der Nutzer seit dem Leeren (onMutate)
      // nichts Neues getippt hat — sonst würde frisch getippter Text überschrieben.
      // Der AKTUELLE Stand kommt aus dem Ref (Closure-Wert wäre veraltet).
      if (ctx?.prevText && composeTextRef.current.trim() === '') {
        setComposeText(ctx.prevText);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messagesKey });
    },
  });

  const addNote = useMutation({
    mutationFn: async () => {
      if (!conversationId) throw new Error('no conversation');
      const content = noteText.trim();
      if (!content) throw new Error('Bitte eine Notiz eingeben.');
      return api.post<InternalNote>(`/api/conversations/${conversationId}/notes`, {
        content,
      });
    },
    onSuccess: () => {
      setNoteText('');
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const suggestReply = useMutation({
    mutationFn: () => api.post(`/api/conversations/${conversationId}/suggest-reply`, {}),
    onSuccess: () => toast.success('KI-Vorschlag wird generiert…'),
    onError: (err: Error) => toast.error(err.message),
  });

  // Terminbuchung ("Stufe A") — Termine dieser Conversation (für dezente Anzeige).
  const { data: appointmentsData } = useQuery({
    queryKey: ['appointments', conversationId],
    queryFn: () =>
      api.get<{ items: AppointmentInfo[] }>(`/api/appointments?conversationId=${conversationId}`),
    enabled: Boolean(conversationId),
  });

  const offerAppointment = useMutation({
    mutationFn: () => api.post(`/api/appointments/offer`, { conversationId }),
    onSuccess: () => {
      toast.success('Terminvorschläge wurden an den Kontakt gesendet.');
      queryClient.invalidateQueries({ queryKey: ['appointments', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
    },
    onError: (err: Error) => {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 422) toast.error('Terminbuchung ist nicht konfiguriert (keine Buchungs-Mailbox).');
      else if (status === 409) toast.error('Aktuell sind keine freien Termine verfügbar.');
      else if (status === 502) toast.error('Der Kalender ist gerade nicht erreichbar.');
      else toast.error(err.message);
    },
  });

  const latestBooked = (appointmentsData?.items ?? []).find((a) => a.status === 'BOOKED');
  const hasPending = (appointmentsData?.items ?? []).some((a) => a.status === 'PROPOSED');

  const addReaction = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      api.post(`/api/messages/${messageId}/reactions`, { emoji }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const removeReaction = useMutation({
    mutationFn: ({ messageId }: { messageId: string }) =>
      api.del(`/api/messages/${messageId}/reactions`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] }),
    onError: (err: Error) => toast.error(err.message),
  });

  // Stabil halten (useCallback), damit das per React.memo umschlossene MessageBubble nicht
  // bei jedem Render eine neue Callback-Referenz bekommt. addReaction/removeReaction.mutate
  // sind über Renders stabil (TanStack Query v5).
  const onToggleReaction = useCallback(
    (messageId: string, emoji: string, currentlyMine: boolean) => {
      if (currentlyMine) removeReaction.mutate({ messageId });
      else addReaction.mutate({ messageId, emoji });
    },
    [addReaction, removeReaction],
  );

  async function onFilePicked(file: File) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const asset = await api.upload<{ id: string; mimeType: string; filename: string | null }>(
        '/api/uploads',
        form,
      );
      setPendingUpload(asset);
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        toast.error('Datei zu groß.');
      } else {
        toast.error(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function insertAtCursor(text: string) {
    const el = composeRef.current;
    if (!el) {
      setComposeText((prev) => prev + text);
      return;
    }
    const start = el.selectionStart ?? composeText.length;
    const end = el.selectionEnd ?? composeText.length;
    // „/" am Cursor (Trigger) durch den Baustein ersetzen, falls vorhanden.
    const before = composeText.slice(0, start);
    const trimmedBefore = before.endsWith('/') ? before.slice(0, -1) : before;
    const next = trimmedBefore + text + composeText.slice(end);
    setComposeText(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = trimmedBefore.length + text.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function onSubmit() {
    if (!composeText.trim() && !selectedTemplate && !pendingUpload) return;
    if (selectedTemplate) {
      // Hat die Vorlage Platzhalter → erst Variablen-Dialog; sonst direkt mit [].
      if (needsTemplateVars(selectedTemplate)) {
        setVarsDialogOpen(true);
        return;
      }
      sendTemplate([]);
      return;
    }
    // Freitext wird IMMER zugelassen (keine harte UI-Sperre). Außerhalb des 24h-Fensters lehnt
    // WhatsApp ihn zwar ab → die Nachricht landet dann als FAILED mit klarer Begründung (Worker),
    // statt dass der Chat UI-seitig tot ist.
    sendMessage.mutate({
      contentText: composeText.trim() || undefined,
      upload: pendingUpload,
    });
  }

  /**
   * Sendet die gewählte Vorlage mit den (per Dialog) ausgefüllten, positionssortierten
   * Body-Variablen. Optionale Komponenten-Werte (Header-Variable / per-Send-Medium /
   * dynamische Buttons) werden nur durchgereicht, wenn der Dialog sie geliefert hat.
   */
  function sendTemplate(
    orderedVars: string[],
    extras?: {
      headerVars?: string[];
      headerMedia?: { mediaId: string };
      buttonParams?: Array<{ subType: 'url' | 'quick_reply'; index: number; value: string }>;
    },
  ) {
    if (!selectedTemplate) return;
    sendMessage.mutate({
      templateName: selectedTemplate.name,
      templateLang: selectedTemplate.language,
      vars: orderedVars,
      headerVars: extras?.headerVars,
      headerMedia: extras?.headerMedia,
      buttonParams: extras?.buttonParams,
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === '/' && composeText.length === 0) {
      // „/" am Zeilenanfang öffnet die Schnellantworten.
      setQuickRepliesOpen(true);
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  }

  if (!conversationId) {
    return (
      <div className="flex h-full items-center justify-center bg-kalkuel-surface">
        <EmptyState
          icon={<MessagesSquare size={22} />}
          title="Keine Konversation gewählt"
          description="Wähle links eine Konversation aus, um den Verlauf zu sehen und zu antworten."
        />
      </div>
    );
  }
  if (detailError) {
    return (
      <div className="flex h-full items-center justify-center bg-kalkuel-surface">
        <ErrorState
          message="Die Konversation konnte nicht geladen werden."
          onRetry={() => refetchDetail()}
        />
      </div>
    );
  }
  if (!detail || detailLoading) {
    return <ConversationSkeleton />;
  }

  // Tagesbezug für die Datums-Trenner-Labels („Heute"/„Gestern"/Datum). Bewusst pro
  // Render frisch — billig und korrekt über Tageswechsel hinweg.
  const now = new Date();

  const approvedTemplates = templates?.items.filter((t) => t.metaStatus === 'APPROVED') ?? [];

  // WARNUNG: min-h-0, grid-rows-[minmax(0,1fr)], overflow-hidden + section min-h-0
  // NICHT entfernen — ohne sie expandiert die flex-Section über den Viewport und
  // der Composer (Textarea + Senden-Button) landet außerhalb des sichtbaren Bereichs.
  // Schon zweimal regressed: 877e8e1b (Erstfix), 74083da4 (Wiederfix nach 3c99c0b4).
  return (
    <div className="grid h-full min-h-0 grid-cols-[1fr_300px] grid-rows-[minmax(0,1fr)] overflow-hidden bg-kalkuel-surface">
      <section className="flex h-full min-h-0 flex-col">
        <header className="flex items-center justify-between border-b border-kalkuel-border bg-white bg-header-gradient px-5 py-3 shadow-card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-gradient text-sm font-semibold text-white shadow-card">
              {detail.contact.displayName.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-semibold text-kalkuel-ink">
                {detail.contact.displayName}
              </div>
              <div className="text-xs text-kalkuel-muted">
                {detail.channelAccount.displayName} · {TEAM_LABELS[detail.assignedTeam] ?? detail.assignedTeam}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {detail.tags.map((t) => (
              <span
                key={t.tag.id}
                style={{ backgroundColor: t.tag.color + '22', color: t.tag.color }}
                className="inline-flex h-5 max-w-[140px] items-center truncate rounded-pill px-2 text-[10px] font-medium uppercase leading-none tracking-wider"
              >
                {t.tag.name}
              </span>
            ))}
            {detail.isImportant && (
              <Star size={14} aria-label="wichtig" className="fill-amber-400 text-amber-400" />
            )}
            {latestBooked?.startsAt && (
              <Badge tone="success">
                <CalendarClock size={11} className="mr-1 inline" />
                Termin {formatDate(latestBooked.startsAt)} {formatTime(latestBooked.startsAt)}
              </Badge>
            )}
            {!latestBooked && hasPending && (
              <Badge tone="warning">
                <CalendarClock size={11} className="mr-1 inline" />
                Termin angeboten
              </Badge>
            )}
            <Badge tone={detail.status === 'OPEN' ? 'accent' : 'neutral'}>{detail.status}</Badge>
            <ConversationActionsMenu
              conversation={{
                id: detail.id,
                status: detail.status,
                isImportant: detail.isImportant,
                deletedAt: detail.deletedAt,
                channelAccount: { id: detail.channelAccount.id },
                tags: detail.tags,
              }}
              variant="header"
            />
          </div>
        </header>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="flex-1 overflow-y-auto px-6 py-4"
          >
            {/* Sentinel oben: lädt beim Hochscrollen ältere Nachrichten nach. */}
            <div ref={olderSentinelRef} aria-hidden />
            {isFetchingOlder && (
              <div className="py-2 text-center text-xs text-kalkuel-muted">
                Lädt ältere Nachrichten…
              </div>
            )}
            {streamRows.length === 0 && !contactTyping ? (
              <EmptyState
                icon={<MessagesSquare size={22} />}
                title="Noch keine Nachrichten"
                description="Schreibe unten die erste Nachricht, um die Konversation zu beginnen."
              />
            ) : (
              streamRows.map(({ entry, showDateSeparator, groupedWithPrev, isLastOfGroup }) => (
                <div key={`${entry.kind}-${entry.item.id}`}>
                  {showDateSeparator && (
                    <DateSeparator label={dateSeparatorLabel(new Date(entry.item.createdAt), now)} />
                  )}
                  {entry.kind === 'msg' ? (
                    <MessageBubble
                      msg={entry.item as Message}
                      myUserId={me?.sub ?? null}
                      grouped={groupedWithPrev}
                      showTimestamp={isLastOfGroup}
                      onToggleReaction={onToggleReaction}
                    />
                  ) : (
                    <NoteBubble note={entry.item as InternalNote} />
                  )}
                </div>
              ))
            )}
            {contactTyping && (
              <div className="mt-2">
                <TypingBubble />
              </div>
            )}
          </div>
          {showNewBadge && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-pill bg-kalkuel-primary px-3 py-1.5 text-xs font-medium text-white shadow-pop transition-colors animate-pop-in hover:bg-kalkuel-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
            >
              <ArrowDown size={12} />
              Neue Nachrichten
            </button>
          )}
        </div>

        <div className="border-t border-kalkuel-border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs">
              {isWhatsApp && (
                <button
                  type="button"
                  onClick={() => setTemplateGalleryOpen(true)}
                  className="flex items-center gap-1 rounded-pill bg-kalkuel-surface px-3 py-1 text-xs font-medium text-kalkuel-primary transition-colors hover:bg-kalkuel-accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
                >
                  <LayoutTemplate size={11} />
                  Vorlage
                </button>
              )}
              <div className="relative">
                <button
                  onClick={() => setQuickRepliesOpen((o) => !o)}
                  className="flex items-center gap-1 rounded-pill bg-kalkuel-surface px-3 py-1 text-xs font-medium text-kalkuel-primary hover:bg-kalkuel-accent-soft"
                >
                  <MessageSquareReply size={11} />
                  Schnellantworten
                </button>
                <QuickReplyPicker
                  open={quickRepliesOpen}
                  onClose={() => setQuickRepliesOpen(false)}
                  onSelect={(body) => insertAtCursor(body)}
                />
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => offerAppointment.mutate()}
                disabled={offerAppointment.isPending}
                title="Dem Kontakt freie Terminvorschläge senden"
              >
                <CalendarClock size={12} />
                Termin anbieten
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => suggestReply.mutate()}
                disabled={suggestReply.isPending}
              >
                <Sparkles size={12} />
                KI-Vorschlag
              </Button>
            </div>
          </div>

          {windowClosed && !selectedTemplate && (
            <div className="mb-2 flex items-center gap-1.5 rounded-field border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 animate-fade-in">
              <CalendarClock size={13} className="shrink-0 text-amber-600" />
              <span className="min-w-0 flex-1">
                24-Std-Fenster zu – Freitext evtl. nicht zustellbar.
              </span>
              <InfoHint
                side="top"
                label="Warum das 24-Stunden-Fenster wichtig ist"
                content={
                  <>
                    WhatsApp lässt außerhalb des 24-Stunden-Fensters nur genehmigte Vorlagen zu.
                    Vorlagen sind aktuell durch eine fehlende Meta-Zahlungsmethode blockiert.
                    Senden ist trotzdem möglich – schlägt es fehl, siehst du den Grund direkt an
                    der Nachricht. Am sichersten ist es, zu warten, bis der Kontakt wieder schreibt.
                  </>
                }
              />
            </div>
          )}

          {aiSuggestion !== null && (
            <div className="mb-2 rounded-field border border-kalkuel-accent/40 bg-kalkuel-accent-soft p-3 shadow-card animate-slide-up">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-kalkuel-ink">
                  <Sparkles size={12} className="text-kalkuel-accent-strong" />
                  KI-Vorschlag
                </span>
                <button
                  onClick={() => setAiSuggestion(null)}
                  className="rounded-pill p-0.5 text-kalkuel-muted transition-colors hover:bg-white/60 hover:text-kalkuel-primary"
                  title="Verwerfen"
                >
                  <X size={12} />
                </button>
              </div>
              <div className="mb-2 whitespace-pre-wrap text-sm text-kalkuel-primary">
                {aiSuggestion}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  size="sm"
                  onClick={() => {
                    setComposeText(aiSuggestion);
                    setAiSuggestion(null);
                  }}
                >
                  Übernehmen
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setComposeText(aiSuggestion);
                    setAiSuggestion(null);
                    composeRef.current?.focus();
                  }}
                >
                  Bearbeiten
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAiSuggestion(null)}>
                  Verwerfen
                </Button>
              </div>
            </div>
          )}
          {selectedTemplate && (
            <div className="mb-2 rounded-field border border-kalkuel-accent/30 bg-kalkuel-accent-soft p-3 text-xs shadow-card animate-slide-up">
              <div className="mb-1 flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-semibold text-kalkuel-ink">
                  <LayoutTemplate size={12} className="text-kalkuel-accent-strong" />
                  Vorlage: {templateDisplayName(selectedTemplate)}
                </span>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="rounded-pill p-0.5 text-kalkuel-muted transition-colors hover:bg-white/60 hover:text-kalkuel-primary"
                  title="Entfernen"
                >
                  <X size={12} />
                </button>
              </div>
              <div className="whitespace-pre-wrap text-kalkuel-muted">
                {selectedTemplate.body}
              </div>
              {needsTemplateVars(selectedTemplate) && (
                <div className="mt-2 text-[11px] font-medium text-kalkuel-accent-strong">
                  Variablen werden vor dem Senden abgefragt.
                </div>
              )}
            </div>
          )}

          {pendingUpload && (
            <div className="mb-2 inline-flex items-center gap-2 rounded-field border border-kalkuel-border bg-kalkuel-surface-2 px-3 py-1.5 text-xs text-kalkuel-primary shadow-card animate-fade-in">
              <Paperclip size={12} className="text-kalkuel-accent-strong" />
              <span className="max-w-[200px] truncate">
                {pendingUpload.filename ?? pendingUpload.mimeType}
              </span>
              <button
                onClick={() => setPendingUpload(null)}
                className="rounded-pill p-0.5 text-kalkuel-muted transition-colors hover:bg-white hover:text-kalkuel-primary"
                title="Entfernen"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <div className="relative">
            <textarea
              ref={composeRef}
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Nachricht schreiben…"
              className="h-24 w-full resize-none rounded-field border border-kalkuel-border p-3 pb-7 text-sm transition-shadow focus:border-kalkuel-accent focus:outline-none focus:shadow-focus-accent"
            />
            <span className="pointer-events-none absolute bottom-2 right-3 select-none text-[10px] text-kalkuel-muted/70">
              Strg+Enter senden · / für Schnellantworten
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFilePicked(f);
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-pill p-1.5 text-kalkuel-muted hover:bg-kalkuel-surface disabled:cursor-not-allowed disabled:opacity-50"
                title="Anhang hochladen"
              >
                {uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Paperclip size={14} />
                )}
              </button>
            </div>
            <Button onClick={onSubmit} disabled={sendMessage.isPending}>
              <Send size={12} />
              Senden
            </Button>
          </div>
        </div>
      </section>

      <aside className="overflow-y-auto border-l border-kalkuel-border bg-white p-4 shadow-card">
        <div className="mb-4">
          <div className="kalkuel-eyebrow">Kontakt</div>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-kalkuel-primary">
              <Phone size={12} className="text-kalkuel-muted" />
              {detail.contact.phones[0] ?? '—'}
            </div>
            <div className="flex items-center gap-2 text-xs text-kalkuel-primary">
              <Mail size={12} className="text-kalkuel-muted" />
              {detail.contact.emails[0] ?? '—'}
            </div>
          </div>
        </div>
        {detail.contact.links.length > 0 && (
          <div className="mb-4">
            <div className="kalkuel-eyebrow">Verknüpfungen</div>
            <ul className="mt-2 space-y-1.5">
              {detail.contact.links.map((l) => (
                <li key={l.id} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <Badge tone="accent">{l.system}</Badge>
                    <span className="text-kalkuel-muted">{l.externalId}</span>
                  </span>
                  {l.externalRef && (
                    <a
                      href={l.externalRef}
                      target="_blank"
                      rel="noreferrer"
                      className="text-kalkuel-accent2 hover:text-kalkuel-accent"
                    >
                      <ExternalLink size={11} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ConversationCrmSidebar contactId={detail.contact.id} />

        {/* Interne Notizen — bewusst HIER (rechte Spalte), NICHT im Sende-Composer, damit eine
            Notiz nie versehentlich als Antwort an den Kontakt/Bewerber rausgeht. Notizen sind nur
            intern und erscheinen zusätzlich (amber markiert) im Verlauf. */}
        <div className="mb-4">
          <div className="kalkuel-eyebrow flex items-center gap-1.5 text-amber-700">
            <StickyNote size={11} />
            Interne Notiz
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                addNote.mutate();
              }
            }}
            placeholder="Nur intern – sichtbar fürs Team, nicht für den Kontakt…"
            className="mt-2 h-20 w-full resize-none rounded-field border border-amber-300 bg-amber-50/40 p-2.5 text-xs text-kalkuel-primary placeholder:text-amber-700/50 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
          />
          <div className="mt-1.5 flex justify-end">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => addNote.mutate()}
              disabled={!noteText.trim() || addNote.isPending}
            >
              <StickyNote size={12} />
              Notiz speichern
            </Button>
          </div>
        </div>

        {isWhatsApp && (
          <div className="mb-4">
            <div className="kalkuel-eyebrow">24-Stunden-Fenster</div>
            <div className="mt-1 text-xs">
              {windowOpen ? (
                <span className="text-emerald-600">
                  Offen{lastInbound ? ` · seit ${formatRelative(lastInbound.createdAt)}` : ''}
                </span>
              ) : (
                <span className="text-amber-700">Geschlossen</span>
              )}
            </div>
          </div>
        )}
        <div className="mb-4">
          <div className="kalkuel-eyebrow">Erstellt</div>
          <div className="mt-1 text-xs text-kalkuel-muted">{formatDate(detail.lastMsgAt)}</div>
        </div>
      </aside>

      <TemplateGalleryDialog
        open={templateGalleryOpen}
        templates={approvedTemplates}
        onClose={() => setTemplateGalleryOpen(false)}
        onPick={(t) => setSelectedTemplate(t)}
      />

      {selectedTemplate && (
        <TemplateVarsDialog
          template={selectedTemplate}
          contact={detail.contact}
          open={varsDialogOpen}
          onClose={() => setVarsDialogOpen(false)}
          onConfirm={(result) => {
            setVarsDialogOpen(false);
            if (result.mode === 'template') {
              sendTemplate(result.vars, {
                headerVars: result.headerVars,
                headerMedia: result.headerMedia,
                buttonParams: result.buttonParams,
              });
            } else {
              // „Frei bearbeitet" → als normale Nachricht (Freitext) senden, nicht als Vorlage.
              setSelectedTemplate(null);
              const text = result.text.trim();
              if (text) sendMessage.mutate({ contentText: text });
            }
          }}
        />
      )}
    </div>
  );
}

function mediaTypeForMime(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

function StatusIndicator({ status, errorReason }: { status: Message['status']; errorReason: string | null }) {
  if (status === 'FAILED') {
    return (
      <span
        className="ml-1 inline-flex items-center gap-0.5 rounded-pill bg-red-500/90 px-1.5 py-0.5 text-[9px] font-medium text-white"
        title={errorReason ?? 'Senden fehlgeschlagen'}
      >
        <AlertCircle size={9} />
        Fehlgeschlagen
        {errorReason && <span className="ml-0.5 hidden sm:inline">· {errorReason}</span>}
      </span>
    );
  }
  if (status === 'SENDING') {
    return <span className="ml-1 inline-flex items-center text-white/60" title="Wird gesendet"><Loader2 size={11} className="animate-spin" /></span>;
  }
  if (status === 'READ') {
    return <span className="ml-1 inline-flex items-center text-kalkuel-accent" title="Gelesen"><CheckCheck size={12} /></span>;
  }
  if (status === 'DELIVERED') {
    return <span className="ml-1 inline-flex items-center text-white/70" title="Zugestellt"><CheckCheck size={12} /></span>;
  }
  // SENT
  return <span className="ml-1 inline-flex items-center text-white/70" title="Gesendet"><Check size={12} /></span>;
}

/** Zentrierter, dezenter Datums-Trenner (WhatsApp-Stil). */
const DateSeparator = memo(function DateSeparator({ label }: { label: string }) {
  return (
    <div className="my-3 flex justify-center">
      <span className="rounded-pill bg-white/85 px-3 py-0.5 text-[11px] font-medium text-kalkuel-muted shadow-card ring-1 ring-kalkuel-border/60">
        {label}
      </span>
    </div>
  );
});

// React.memo: re-rendert nur, wenn sich Props wirklich ändern. Voraussetzung: stabile
// Callback-Referenz (onToggleReaction via useCallback). `msg` ist je Render ein neues
// Objekt NUR, wenn der useInfiniteQuery-Cache neue Daten liefert (Realtime-Invalidierung
// oder optimistisches setQueryData) → genau dann SOLL neu gerendert werden. Beim Tippen
// im Composer o.ä. bleibt die `msg`-Referenz identisch → kein Re-Render. Der Default-
// Shallow-Vergleich von memo genügt hier; kein eigener Comparator nötig (er würde sonst
// echte Updates verschlucken). onToggleReaction erhält jetzt die messageId mit.
const MessageBubble = memo(function MessageBubble({
  msg,
  myUserId,
  grouped,
  showTimestamp,
  onToggleReaction,
}: {
  msg: Message;
  myUserId: string | null;
  /** Eng an die Vor-Nachricht desselben Absenders anhängen (kleinerer Abstand). */
  grouped: boolean;
  /** Zeitmarke dauerhaft zeigen (letztes Element der Gruppe). Sonst Uhrzeit als Hover-Tooltip. */
  showTimestamp: boolean;
  onToggleReaction: (messageId: string, emoji: string, currentlyMine: boolean) => void;
}) {
  const outbound = msg.direction === 'OUTBOUND';
  const failed = msg.status === 'FAILED';
  const isAi = msg.senderKind === 'AI';
  const attachments = Array.isArray(msg.attachments) ? (msg.attachments as Attachment[]) : [];
  // Die Zeit-/Status-Zeile wird REFLOW-FREI behandelt: Sie wird genau dann gerendert,
  // wenn sie dauerhaft sichtbar ist — am letzten Element einer Gruppe (eine Zeitmarke
  // je Gruppe) oder bei Fehlern. Sie wird NICHT per Hover ein-/ausgeklappt (das wäre
  // ein Höhensprung). Stattdessen trägt die Bubble der Vorgänger einer Gruppe die
  // genaue Uhrzeit als `title` → Hover zeigt die Zeit als Tooltip, ohne Layout-Shift.
  const metaVisible = showTimestamp || failed;
  // Auf die Signatur der Reaktions-Komponenten ((emoji, mine) => void) adaptieren und die
  // messageId fest binden. Stabil, solange msg.id + onToggleReaction stabil sind.
  const toggleForThisMsg = useCallback(
    (emoji: string, mine: boolean) => onToggleReaction(msg.id, emoji, mine),
    [onToggleReaction, msg.id],
  );
  return (
    <div
      className={[
        'group relative flex flex-col',
        outbound ? 'items-end' : 'items-start',
        grouped ? 'mt-0.5' : 'mt-3',
      ].join(' ')}
    >
      <div
        title={metaVisible ? undefined : formatTime(msg.createdAt)}
        className={[
          'relative max-w-[70%] px-4 py-2.5 text-sm shadow-card',
          // Gruppierte Folgenachrichten: die zur Gruppen-Innenseite zeigende obere Ecke
          // wird „eckiger" (WhatsApp-Anmutung eines zusammenhängenden Blocks).
          grouped
            ? outbound
              ? 'rounded-2xl rounded-tr-md'
              : 'rounded-2xl rounded-tl-md'
            : 'rounded-2xl',
          failed
            ? 'border border-red-300 bg-red-50 text-kalkuel-primary'
            : outbound
              ? 'bg-kalkuel-primary text-white'
              : 'bg-white text-kalkuel-primary border border-kalkuel-border',
        ].join(' ')}
      >
        {msg.contentText && (
          <div className="whitespace-pre-wrap">
            <Linkified text={msg.contentText} />
          </div>
        )}
        {attachments.map((att, i) => (
          <MessageAttachment key={i} attachment={att} outbound={outbound} messageId={msg.id} />
        ))}
        {metaVisible && (
          <div
            className={[
              'mt-1 flex items-center gap-1.5 text-[10px]',
              failed ? 'text-red-700' : outbound ? 'text-white/70' : 'text-kalkuel-muted',
            ].join(' ')}
          >
            {isAi && (
              <span
                className={[
                  'inline-flex items-center gap-1 rounded-pill px-1.5 py-0.5 font-medium',
                  failed ? 'bg-red-100 text-red-700' : 'bg-white/20 text-white',
                ].join(' ')}
                title="Automatisch von der KI beantwortet"
              >
                <Sparkles size={9} />
                KI-Antwort
              </span>
            )}
            {formatTime(msg.createdAt)}
            {outbound && <StatusIndicator status={msg.status} errorReason={msg.errorReason} />}
          </div>
        )}
        <ReactionTrigger
          reactions={msg.reactions ?? []}
          myUserId={myUserId}
          outbound={outbound}
          onToggle={toggleForThisMsg}
        />
      </div>
      <ReactionChips
        reactions={msg.reactions ?? []}
        myUserId={myUserId}
        outbound={outbound}
        onToggle={toggleForThisMsg}
      />
    </div>
  );
});

/**
 * „tippt gerade…"-Indikator für eine humanisierte Auto-Antwort, die gerade
 * verfasst wird. Wie eine ausgehende Blase (rechts), mit drei pulsierenden Punkten.
 */
function TypingBubble() {
  return (
    <div className="flex flex-col items-end" role="status" aria-live="polite">
      <div className="flex items-center gap-1.5 rounded-2xl bg-kalkuel-primary/80 px-4 py-3 shadow-brand">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80 [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80 [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/80" />
      </div>
      <div className="mt-1 text-[10px] text-kalkuel-muted">Automatische Antwort wird verfasst…</div>
    </div>
  );
}

const NoteBubble = memo(function NoteBubble({ note }: { note: InternalNote }) {
  return (
    <div className="mx-auto max-w-[80%] rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <div className="mb-0.5 flex items-center gap-1 font-medium">
        <StickyNote size={11} /> Interne Notiz
      </div>
      <div className="whitespace-pre-wrap">
        <Linkified text={note.content} />
      </div>
      <div className="mt-1 text-[10px] text-amber-700">{formatTime(note.createdAt)}</div>
    </div>
  );
});

/**
 * Hochwertiges Lade-Skelett für den Konversations-Verlauf: Kopfzeile + ein paar
 * alternierende Bubble-Platzhalter (eingehend links / ausgehend rechts) + ein
 * gedämpfter Composer-Streifen. Vermeidet rohen Text/Sprünge beim Laden.
 */
function ConversationSkeleton() {
  // Feste, abwechslungsreiche Breiten für ein natürliches Bubble-Bild.
  const rows: Array<{ out: boolean; w: string }> = [
    { out: false, w: 'w-2/5' },
    { out: true, w: 'w-1/2' },
    { out: false, w: 'w-3/5' },
    { out: false, w: 'w-1/3' },
    { out: true, w: 'w-2/5' },
    { out: false, w: 'w-1/2' },
  ];
  return (
    <div className="flex h-full min-h-0 flex-col bg-kalkuel-surface" aria-hidden>
      <div className="flex items-center gap-3 border-b border-kalkuel-border bg-white px-5 py-3">
        <div className="h-10 w-10 animate-pulse rounded-full bg-kalkuel-border/60" />
        <div className="space-y-1.5">
          <div className="h-3.5 w-40 animate-pulse rounded bg-kalkuel-border/60" />
          <div className="h-2.5 w-28 animate-pulse rounded bg-kalkuel-border/40" />
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-hidden px-6 py-4">
        {rows.map((r, i) => (
          <div key={i} className={['flex', r.out ? 'justify-end' : 'justify-start'].join(' ')}>
            <div
              className={[
                'h-12 animate-pulse rounded-2xl',
                r.w,
                r.out ? 'bg-kalkuel-primary/15' : 'bg-kalkuel-border/50',
              ].join(' ')}
            />
          </div>
        ))}
      </div>
      <div className="border-t border-kalkuel-border bg-white p-4">
        <div className="h-24 w-full animate-pulse rounded-md bg-kalkuel-border/40" />
      </div>
    </div>
  );
}
